import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Stack, Text } from '@figma/fpl-components';
import { ProgressPrimitive } from '@figma/fpl-components/beta';
import { Icon24Attention, Icon24LogoWeavy } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { getNormalizedItemHeight, MasonryGrid } from '@/UI/MasonryGrid/MasonryGrid';
import { useContentRect } from '@/hooks/useContentRect';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import { setMediaPanelDragFiles, setThumbnailDragPicture } from '@/utils/mediaPanelDrag.utils';
import { ToolOutputPreview, type ToolOutputPreviewSelection } from './ToolOutputPreview';
import { ToolOutputBatchActions } from './ToolOutputBatchActions';
import {
  groupToolOutputs,
  isToolOutputPlaceholder,
  type ToolOutputEntry,
  type ToolOutputPlaceholder,
} from './ToolOutputGroups';
import { ToolOutputActions, type ToolOutputRemovalTarget } from './ToolOutputActions';
import type { DesignAppResult } from '@/types/design-app.types';

const logger = log.getLogger('ToolOutputHistory');
const MIN_OUTPUT_WIDTH = 240;
const OUTPUT_GAP = 16;
/** Width over height, for a placeholder whose run didn't ask for a shape. */
const DEFAULT_PLACEHOLDER_ASPECT_RATIO = 16 / 9;

const placeInRowOrder = () => 1;

function getOutputHeight(result: ToolOutputEntry): number {
  if (isToolOutputPlaceholder(result) || (result.type !== 'image' && result.type !== 'video')) return 1;
  const { width, height } = result;
  if (
    width === undefined ||
    height === undefined ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  )
    return 1;
  return getNormalizedItemHeight({ width, height });
}

interface ToolOutputHistoryProps {
  /** Placeholders show in their batch, where their result will be. */
  results: readonly ToolOutputEntry[];
  recipeName: string;
  /**
   * Removes results on the server and from `results`, rejecting when it fails. Without it, outputs have no
   * Remove action.
   */
  onRemoveResults?: (targets: ToolOutputRemovalTarget[]) => Promise<void>;
  disableRemove?: boolean;
  isLoading: boolean;
  progress?: number;
  /** Max height of the image of a single-output run, as a CSS length, e.g. `'50vh'` or `'200px'`. */
  singleImageMaxHeight?: string;
  /**
   * Places outputs in plain row order instead of by height, so a card that gets its result never moves to
   * another column. Fits outputs that share one shape, such as the runs of one Generate.
   */
  placeInOrder?: boolean;
  /** Each media output can be dragged, carrying its asset, the same as a drag from the Assets panel. */
  draggableResults?: boolean;
}

export function ToolOutputHistory({
  results,
  recipeName,
  onRemoveResults,
  disableRemove = false,
  isLoading,
  progress,
  singleImageMaxHeight,
  placeInOrder = false,
  draggableResults = false,
}: ToolOutputHistoryProps) {
  const { t } = useTranslation();
  const historyRef = useRef<HTMLDivElement>(null);
  const contentRect = useContentRect(historyRef);
  const containerWidthWithGap = (contentRect?.width ?? 0) + OUTPUT_GAP;
  const minColumnWidthWithGap = MIN_OUTPUT_WIDTH + OUTPUT_GAP;
  const availableColumns = Math.max(1, Math.floor(containerWidthWithGap / minColumnWidthWithGap));
  const labels = I18N_KEYS.SHARED_DESIGN_APP.OUTPUT_HISTORY;
  const [preview, setPreview] = useState<ToolOutputPreviewSelection | null>(null);
  const [timestampNow, setTimestampNow] = useState(Date.now);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletionPending = useRef(false);
  const removalState = useRef({ results, busy: disableRemove });
  removalState.current = { results, busy: disableRemove };
  const groups = groupToolOutputs(results);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const showRemoveFailed = () =>
    updateSnackbarData({ text: t(labels.REMOVE_FAILED), icon: null, severity: 'error', isOpen: true, action: null });

  const deleteBatch = async (batchId: string) => {
    if (!onRemoveResults || removalState.current.busy || deletionPending.current || !batchId) return;
    const batchResults = removalState.current.results.filter(
      (result): result is DesignAppResult => !isToolOutputPlaceholder(result) && result.batchId === batchId,
    );
    if (!batchResults.length || batchResults.some((result) => typeof result.id !== 'string' || !result.id)) return;
    deletionPending.current = true;
    setIsDeleting(true);
    try {
      await onRemoveResults(batchResults.map(({ id, runId }) => ({ id, runId })));
    } catch (error) {
      logger.error('deleteBatch(): error', error);
      showRemoveFailed();
    } finally {
      deletionPending.current = false;
      setIsDeleting(false);
    }
  };

  const deleteOutput = async (target: ToolOutputRemovalTarget) => {
    if (!onRemoveResults || removalState.current.busy || deletionPending.current) {
      return false;
    }
    deletionPending.current = true;
    setIsDeleting(true);
    try {
      await onRemoveResults([target]);
      return true;
    } catch (error) {
      logger.error('deleteOutput(): error', error);
      showRemoveFailed();
      return false;
    } finally {
      deletionPending.current = false;
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    // Refresh relative run timestamps once a minute, even when no new results arrive.
    const timer = setInterval(() => setTimestampNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const progressValue = progress === undefined ? undefined : Math.min(100, Math.max(0, progress));
  const renderLoadingCard = (placeholder?: { key: string; style: CSSProperties }) => (
    <Stack.Col
      key={placeholder?.key}
      align="center"
      justify="center"
      gap={cssVars.spacer.x8px}
      {...stylex.props(styles.loadingCard, placeholder && styles.placeholderCard)}
      style={placeholder?.style}
    >
      <Icon24LogoWeavy />
      <ProgressPrimitive
        aria-label={t(labels.GENERATING)}
        value={progressValue}
        max={100}
        {...stylex.props(styles.progressTrack)}
      >
        {progressValue === undefined ? (
          <div {...stylex.props(styles.progressIndeterminate)} />
        ) : (
          <div {...stylex.props(styles.progressFill)} style={{ width: `${progressValue}%` }} />
        )}
      </ProgressPrimitive>
      {progressValue !== undefined && (
        <Text.span variant="body-small" color="secondary">
          {progressValue}%
        </Text.span>
      )}
    </Stack.Col>
  );

  return (
    <Stack.Col ref={historyRef} data-testid="tool-output-history" {...stylex.props(styles.history)}>
      {isLoading && (
        <section
          aria-label={t(labels.GENERATING)}
          {...stylex.props(styles.loadingSection, groups.length > 0 && styles.loadingSectionWithHistory)}
        >
          <Text.h2 variant="body-large-strong" {...stylex.props(styles.timestampHeading)}>
            {t(labels.JUST_NOW)}
          </Text.h2>
          {renderLoadingCard()}
        </section>
      )}
      {groups.map((group) => {
        const time = Date.parse(group.createdAt ?? '');
        const timestamp = Number.isFinite(time)
          ? timestampNow - time < 60_000
            ? t(labels.JUST_NOW)
            : getTimeDiffInWords(time)
          : undefined;
        return (
          <section key={group.key} aria-label={timestamp}>
            <div {...stylex.props(styles.batchHeader)}>
              {timestamp && (
                <Text.h2 variant="body-large-strong" {...stylex.props(styles.timestampHeading)}>
                  <time
                    dateTime={group.createdAt}
                    title={new Date(time).toLocaleString()}
                    {...stylex.props(styles.timestamp)}
                  >
                    {timestamp}
                  </time>
                </Text.h2>
              )}
              <ToolOutputBatchActions
                group={group}
                recipeName={recipeName}
                onRemoveBatch={onRemoveResults && ((batchId) => void deleteBatch(batchId))}
                disableRemove={disableRemove || isDeleting}
              />
            </div>
            <MasonryGrid
              dataTestId="tool-output-masonry"
              items={contentRect ? group.entries : []}
              numColumns={Math.max(2, Math.min(3, group.entries.length, availableColumns))}
              getItemHeight={placeInOrder ? placeInRowOrder : getOutputHeight}
              containerStyles={styles.outputGap}
              columnStyles={styles.outputGap}
              renderItem={(result) => {
                if (isToolOutputPlaceholder(result)) {
                  // until the result arrives, the card holds the shape the run asked for
                  const style = {
                    aspectRatio: String(result.aspectRatio ?? DEFAULT_PLACEHOLDER_ASPECT_RATIO),
                    maxHeight: group.entries.length === 1 ? singleImageMaxHeight : undefined,
                  };
                  return result.placeholder === 'loading' ? (
                    renderLoadingCard({ key: result.key, style })
                  ) : (
                    <FailedOutputCard key={result.key} output={result} style={style} />
                  );
                }
                const output = (
                  <ToolOutputActions
                    key={`${result.runId}:${result.id}`}
                    result={result}
                    recipeName={recipeName}
                    onRemove={onRemoveResults && ((target) => void deleteOutput(target))}
                    disableRemove={disableRemove || isDeleting}
                    suspendMediaPlayback={preview !== null}
                    imageMaxHeight={group.entries.length === 1 ? singleImageMaxHeight : undefined}
                    onExpand={() => setPreview({ id: result.id, runId: result.runId })}
                  />
                );
                if (!draggableResults || result.type === 'text') {
                  return output;
                }
                return (
                  <div
                    key={`${result.runId}:${result.id}`}
                    draggable
                    onDragStart={(event) => {
                      setMediaPanelDragFiles(event.dataTransfer, [result]);
                      setThumbnailDragPicture(event.dataTransfer, event.currentTarget);
                    }}
                  >
                    {output}
                  </div>
                );
              }}
            />
          </section>
        );
      })}
      {preview && (
        <ToolOutputPreview
          selection={preview}
          results={groups.flatMap((group) => group.results.map(({ result }) => result))}
          workflowName={recipeName}
          removal={
            onRemoveResults && {
              onRemove: (target) => void deleteOutput(target),
              disabled: disableRemove || isDeleting,
            }
          }
          onSelect={setPreview}
          onClose={() => setPreview(null)}
        />
      )}
    </Stack.Col>
  );
}

function FailedOutputCard({ output, style }: { output: ToolOutputPlaceholder; style: CSSProperties }) {
  const { t } = useTranslation();
  const labels = I18N_KEYS.SHARED_DESIGN_APP.OUTPUT_HISTORY;
  return (
    <Stack.Col
      align="center"
      justify="center"
      gap={cssVars.spacer.x8px}
      {...stylex.props(styles.loadingCard, styles.placeholderCard)}
      style={style}
    >
      <Icon24Attention />
      <Text.p variant="body-medium-strong" align="center" {...stylex.props(styles.failedText)}>
        {t(labels.FAILED_TITLE)}
      </Text.p>
      <Text.p variant="body-medium" color="secondary" align="center" {...stylex.props(styles.failedText)}>
        {output.message ?? t(labels.FAILED_MESSAGE)}
      </Text.p>
    </Stack.Col>
  );
}

const indeterminateSlide = stylex.keyframes({
  '0%': { insetInlineStart: '-40%' },
  '100%': { insetInlineStart: '100%' },
});

const styles = stylex.create({
  history: {
    flex: 1,
    minWidth: 0,
    paddingInline: spacer.spacer40px,
    paddingBottom: spacer.spacer24px,
  },
  loadingSection: {
    flex: 1,
    // Fill the visible panel height even when previous outputs extend below the viewport.
    minHeight: `calc(100cqh - ${spacer.spacer24px})`,
    display: 'flex',
    flexDirection: 'column',
  },
  loadingSectionWithHistory: {
    // Leave room for the previous run's heading and a peek at its outputs below the loader.
    minHeight: 'calc(100cqh - 96px)',
  },
  batchHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacer.spacer8px,
  },
  timestampHeading: {
    paddingBlock: spacer.spacer24px,
    margin: 0,
    color: color.text,
  },
  timestamp: { whiteSpace: 'nowrap' },
  loadingCard: {
    flex: 1,
    minHeight: 0,
    paddingTop: 50,
    paddingBottom: 10,
    paddingInline: 10,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.border,
    borderRadius: 5,
    backgroundColor: color.bg,
    color: color.text,
  },
  placeholderCard: {
    flex: 'none',
    paddingTop: 10,
  },
  failedText: { margin: 0 },
  progressTrack: {
    position: 'relative',
    width: 80,
    height: 8,
    overflow: 'hidden',
    backgroundColor: color.bgSecondary,
    borderRadius: 4,
  },
  progressFill: {
    height: '100%',
    backgroundColor: color.bgBrand,
    borderRadius: 4,
    transition: 'width 200ms ease',
  },
  progressIndeterminate: {
    position: 'absolute',
    insetBlockStart: 0,
    height: '100%',
    width: '40%',
    backgroundColor: color.bgBrand,
    borderRadius: 4,
    animationName: indeterminateSlide,
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  outputGap: { gap: spacer.spacer16px },
});
